import { AnimationVars, Callback, ITweenable, IWrapper, Tween, TweenCallbackType, TweenTarget, TweenType, TweenVars } from "./Tween";
import { gsap } from "gsap/dist/gsap";
import { TweenVarConvertor } from "./TweenVarConverter";

/**
 * Represents an animation timeline.
 *
 * @export
 * @class Timeline
 * @implements {IWrapper}
 * @implements {ITweenable}
 */
export class Timeline implements IWrapper, ITweenable {
    private readonly _timeline: gsap.core.Timeline;
    private _children: Map<any, TimelineChild[]>;

    /**
     * Creates an instance of Timeline.
     * @param {TimelineVars} [vars] The timeline settings.
     * @param {number} [time] The timeline start time in seconds.
     * @memberof Timeline
     */
    constructor(vars?: TimelineVars, time?: number) {
        let convertedVars = TweenVarConvertor.convertTimelineVars(vars);
        this._timeline = new gsap.core.Timeline(convertedVars, time);
        this._children = new Map<any, TimelineChild[]>();
    }

    /**
     * Gets the internally wrapped object.
     *
     * @return {*}  {*}
     * @memberof Tween
     */
    public getWrappedObject(): any {
        return this._timeline;
    }

    /**
     * Gets the type of the tween.
     *
     * @return {*}  {TweenType} The tween type.
     * @memberof Tween
     */
    public getTweenType(): TweenType {
        return TweenType.Timeline;
    }

    /**
     * Adds a tween going from the current object's values TO the given values.
     *
     * @param {TweenTarget} targets The object(s) to animate.
     * @param {number} duration The animation duration in seconds.
     * @param {TweenVars} vars The animation settings.
     * @param {(number | string)} [position] The position of this animation at the timeline.
     * If you pass a number this will set the position as absolute time.
     * ```ts
     * // Insert exactly 3 seconds from the start of the timeline
     * timeline.to(sprite.scale, {x: 0.75 }, 3);
     * ```
     * If you pass a string or omit this parameter:
     * The default position is "+=0", which just means "at the end" of the current timeline.
     * "<" position means the start of previous animation. Think of < as a pointer back to the start of the previous animation.
     * ">" position means the end of the previous animation. Think of > as a pointer to the end of the previous animation.
     * "someLabel" means label position. If the label doesn't exist, it'll be added to the end of the timeline.
     * 
     * You can additionaly use the '+=', '-=' and '%' to any of the string positions.
     * Examples: "+=1", "-=0.3", "someLabel+=3", "+=50%", "<25%" and so on.
     * @return {*}  {Timeline}
     * @memberof Timeline
     */
    public to(targets: TweenTarget, duration: number, vars: TweenVars, position?: number | string): Timeline {
        if (!vars.duration) {
            vars.duration = duration;
        }

        let convertedVars = TweenVarConvertor.convertTweenVars(vars);
        let unwrapped = this.unwrap(targets);
        this._timeline.to(unwrapped, convertedVars, position);
        this.addChild(unwrapped, position, true);
        return this;
    }

    /**
     * Adds a tween going FROM given values to the object's current values.
     *
     * @param {TweenTarget} targets The object(s) to animate.
     * @param {number} duration The animation duration in seconds.
     * @param {TweenVars} vars The animation settings.
     * @param {(number | string)} [position] The position of this animation at the timeline.
     * If you pass a number this will set the position as absolute time.
     * ```ts
     * // Insert exactly 3 seconds from the start of the timeline
     * timeline.from(sprite.scale, {x: 0.75 }, 3);
     * ```
     * If you pass a string or omit this parameter:
     * The default position is "+=0", which just means "at the end" of the current timeline.
     * "<" position means the start of previous animation. Think of < as a pointer back to the start of the previous animation.
     * ">" position means the end of the previous animation. Think of > as a pointer to the end of the previous animation.
     * "someLabel" means label position. If the label doesn't exist, it'll be added to the end of the timeline.
     * 
     * You can additionaly use the '+=', '-=' and '%' to any of the string positions.
     * Examples: "+=1", "-=0.3", "someLable+=3", "+=50%", "<25%" and so on.
     * @return {*}  {Timeline}
     * @memberof Timeline
     */
    public from(targets: TweenTarget, duration: number, vars: TweenVars, position?: number | string): Timeline {
        if (!vars.duration) {
            vars.duration = duration;
        }

        let convertedVars = TweenVarConvertor.convertTweenVars(vars);
        let unwrapped = this.unwrap(targets);
        this._timeline.from(unwrapped, convertedVars, position);
        this.addChild(unwrapped, position, true);
        return this;
    }

    /**
     * Adds a tween going FROM given values TO another given values.
     *
     * @param {TweenTarget} targets The object(s) to animate.
     * @param {number} duration The animation duration in seconds.
     * @param {TweenVars} fromVars The 'from' animation settings.
     * @param {TweenVars} toVars The 'to' animation settings.
     * @param {(number | string)} [position] The position of this animation at the timeline.
     * If you pass a number this will set the position as absolute time.
     * ```ts
     * // Insert exactly 3 seconds from the start of the timeline
     * timeline.from(sprite.scale, {x: 0.75 }, 3);
     * ```
     * If you pass a string or omit this parameter:
     * The default position is "+=0", which just means "at the end" of the current timeline.
     * "<" position means the start of previous animation. Think of < as a pointer back to the start of the previous animation.
     * ">" position means the end of the previous animation. Think of > as a pointer to the end of the previous animation.
     * "someLabel" means label position. If the label doesn't exist, it'll be added to the end of the timeline.
     * 
     * You can additionaly use the '+=', '-=' and '%' to any of the string positions.
     * Examples: "+=1", "-=0.3", "someLable+=3", "+=50%", "<25%" and so on.
     * @return {*}  {Timeline}
     * @memberof Timeline
     */
    public fromTo(targets: TweenTarget, duration: number, fromVars: TweenVars, toVars: TweenVars, position?: number | string): Timeline {
        // this._timeline.fromTo(target, duration, fromVars, toVars, position);
        this.set(targets, fromVars, position);
        this.to(targets, duration, toVars, position);
        return this;
    }

    /**
     * Sets properties of the target(s) to the properties specified at the time of the set call.
     *
     * @param {TweenTarget} targets The object(s) to change.
     * @param {TweenVars} vars The settings to apply to the object(s).
     * @param {(number | string)} [position] The position of this animation at the timeline.
     * If you pass a number this will set the position as absolute time.
     * ```ts
     * // Insert exactly 3 seconds from the start of the timeline
     * timeline.set(sprite.scale, {x: 0.75 }, 3);
     * ```
     * If you pass a string or omit this parameter:
     * The default position is "+=0", which just means "at the end" of the current timeline.
     * "<" position means the start of previous animation. Think of < as a pointer back to the start of the previous animation.
     * ">" position means the end of the previous animation. Think of > as a pointer to the end of the previous animation.
     * "someLabel" means label position. If the label doesn't exist, it'll be added to the end of the timeline.
     * 
     * You can additionaly use the '+=', '-=' and '%' to any of the string positions.
     * Examples: "+=1", "-=0.3", "someLable+=3", "+=50%", "<25%" and so on.
     * @return {*}  {Timeline}
     * @memberof Timeline
     */
    public set(targets: TweenTarget, vars: TweenVars, position?: number | string): Timeline {
        this._timeline.set(targets, vars, position);
        return this;
    }

    /**
     * Plays the animation, optionally from the given start time.
     *
     * ```ts
     * anim.play(1.5);  // play from the 1.5 second mark
     * ``` 
     *
     * @param {(string | number)} [from]
     * @param {boolean} [suppressEvents=false] Pass true to suppress the event calling, otherwise pass false.
     * @return {*}  {Timeline}
     * @memberof Timeline
     */
    public play(from?: string | number, suppressEvents: boolean = false): Timeline {
        this._timeline.play(from, suppressEvents);
        return this;
    }

    /**
     * Returns true or false based on the active state of the animation.
     * Being active means that the virtual playhead is actively moving across
     * this instance's time span and it is not paused, nor are any of its ancestor timelines.
     *
     * @return {*}  {boolean}
     * @memberof Timeline
     */
    public isActive(): boolean {
        return this._timeline.isActive();
    }
    
    /**
     * Empties the timeline of all tweens, timelines, callbacks, and optionally labels.
     *
     * @param {boolean} [labels=false] Pass true to clear the labels too.
     * @return {*}  {Timeline} The same, but cleared timeline.
     * @memberof Timeline
     */
    public clear(labels: boolean = false): Timeline {
        this._timeline.clear(labels);
        return this;
    }

    /**
     * To kill means to immediately stop the animation, remove it from its parent timeline, and release it for garbage collection.
     *
     * @return {*}  {Timeline} Self
     * @memberof Timeline
     */
    public kill(): Timeline {
        this._timeline.kill();
        return this;
    }
    
    /**
     * Pauses the animation, optionally at the given time.
     *
     * ```js
     * anim.pause();    // pause immediately
     * anim.pause(1.5); // pause but seek to this time in the animation
     * ```
     *
     * @param {(string | number)} [atTime] The position to set, when the timeline will stay paused.
     * @param {boolean} [suppressEvents=false] Pass true to suppress the event calls.
     * @return {*}  {Timeline} Self
     * @memberof Timeline
     */
    public pause(atTime?: string | number, suppressEvents: boolean = false): Timeline {
        this._timeline.pause(atTime, suppressEvents);
        return this;
    }

    /**
     * Adds a label, tween, timeline, or an array of those values to the timeline, optionally at the specified time.
     *
     * ```ts
     * tl.add("myLabel");  // add a label at the end of the timeline
     * tl.add(myTween, 1); // add a tween at the 1 second mark
     * tl.add(myTimeline, "-=1"); // add a timeline 1 second before the end of the timeline
     * tl.add(["myLabel", myTween, myTimeline], "<"); // add a label, tween, and timeline at the start of the previous tween
     * ```
     *
     * @param {TimelineChild} value The child to add.
     * @param {(number | string)} [position] The insert position.
     * @return {*}  {Timeline} Self
     * @memberof Timeline
     */
    public add(value: TimelineChild, position?: number | string): Timeline {
        this.addChild(value, position, false);
        return this;
    }

    /**
     * Removes a tween, timeline, callback, label, or array of those values from the timeline.
     *
     * ```ts
     * tl.remove(myTween);
     * tl.remove([myTween, mySubTimeline, "myLabel"]);
     * ```
     *
     * @param {TimelineChild} value The child to remove.
     * @return {*} Self
     * @memberof Timeline
     */
    public remove(value: TimelineChild): Timeline {
        let child = this.unwrap(value);
        this._timeline.remove(child);
        if (this._children.has(child)) {
            let list = this._children.get(child);
            let index = list.indexOf(value);
            if (index != -1) {
                list.splice(index, 1);
            }

            if (list.length == 0) {
                this._children.delete(child);
            }
        }

        return this;
    }

    /**
     *  Continues a paused animation in the direction it was headed (forwards or reverse), optionally from the given time.
     *
     * ```ts
     * anim.resume();  // continues the animation
     * anim.resume(1); // continues the animation from the 1 second mark
     * ```
     *
     * @param {(string | number)} [from] The position to set, when resuing the timeline.
     * @param {boolean} [suppressEvents=false] Pass true to suppress the event calls.
     * @return {*}  {Timeline} Self
     * @memberof Timeline
     */
    public resume(from?: string | number, suppressEvents: boolean = false): Timeline {
        this._timeline.resume(from, suppressEvents);
        return this;
    }

    /**
     * Reverses playback so that all aspects of the animation are oriented backwards including, 
     * for example, a tween's ease.
     * @param from (default = undefined) — The time (or label for Timeline instances) from which the 
     * animation should begin playing in reverse (if none is defined, it will begin playing from 
     * wherever the playhead currently is). To begin at the very end of the animation, use 0. 
     * Negative numbers are relative to the end of the animation, so -1 would be 1 second from the end.
     * @param suppressEvents (default = true) — If true (the default), no events or callbacks 
     * will be triggered when the playhead moves to the new position defined in the from parameter.
     * @returns self
     */
    public reverse(from?: number, suppressEvents: boolean = false): Timeline {
        this._timeline.reverse(from, suppressEvents);
        return this;
    }

    /**
     * Gets or sets the animation's reversed state which indicates whether or not the 
     * animation should be played backwards.
     * @returns Boolean | self
     */
    public reversed(): boolean {
        return this._timeline.reversed();
    }

    /**
     * Gets the current time of the timeline.
     *
     * @type {number}
     * @memberof Timeline
     */
    public get time(): number {
        return this._timeline.time();
    }

    /**
     * Sets the current time of the timeline.
     *
     * @memberof Timeline
     */
    public set time(value: number) {
        this._timeline.time(value);
    }

    /**
     * Gets the paused state of the timeline.
     *
     * @type {boolean}
     * @memberof Timeline
     */
    public get paused(): boolean {
        return this._timeline.paused();
    }

    /**
     * Sets the paused state of the timeline.
     *
     * @memberof Timeline
     */
    public set paused(value: boolean) {
        this._timeline.paused(value);
    }

    /**
     * Gets the progress of the timeline.
     *
     * @type {number}
     * @memberof Timeline
     */
    public get progress(): number {
        return this._timeline.progress();
    }

    /**
     * Sets the progress of the timeline.
     *
     * @memberof Timeline
     */
    public set progress(value: number) {
        this._timeline.progress(value);
    }

    /**
     * Gets the timeline's time scale.
     *
     * @type {number}
     * @memberof Timeline
     */
    public get timeScale(): number {
        return this._timeline.timeScale();
    }

    /**
     * Sets the timeline's time scale.
     *
     * @memberof Timeline
     */
    public set timeScale(value: number) {
        this._timeline.timeScale(value);
    }

    /**
     * Gets the total progress (including repeats), measured in seconds.
     *
     * @type {number}
     * @memberof Timeline
     */
    public get totalDuration(): number {
        return this._timeline.totalDuration();
    }

    /**
     * Sets the total progress (including repeats) in seconds.
     *
     * @memberof Timeline
     */
    public set totalDuration(value: number) {
        this._timeline.totalDuration(value);
    }

    /**
     * Gets the timeline settings.
     *
     * @type {TimelineVars}
     * @memberof Timeline
     */
    public get vars(): TimelineVars {
        return TweenVarConvertor.convertBackTimelineVars(this._timeline.vars);
    }

    /**
     * Sets the timeline settings.
     *
     * @memberof Timeline
     */
    public set vars(value: TimelineVars) {
        this._timeline.vars = TweenVarConvertor.convertTimelineVars(value);
    }

    /**
     * Returns an array containing all the tweens and/or timelines nested in this timeline.
     *
     * @return {*}  {TimelineChild[]}
     * @memberof Timeline
     */
    public getChildren(): TimelineChild[] {
        // Getting the real children
        let children = this._timeline.getChildren();

        // Prune the internal children map
        let childrenToKeep = new Map<any, TimelineChild[]>();
        children.forEach(child => {
            let unwrapped = this.unwrap(child);
            if (this._children.has(unwrapped)) {
                childrenToKeep.set(unwrapped, this._children.get(unwrapped));
            }
        });

        this._children = childrenToKeep;
        let result = new Array<TimelineChild>();
        let matrix = Array.from(this._children.values());
        matrix.forEach((value, index, array) => {
            value.forEach((item) => {
                result.push(item);
            })
        })

        return result;
    }

    /**
     * Call a function, optionally at the specified time.
     *
     * @param {Callback} callback The function to call.
     * @param {any[]} [params] The function arguments to pass.
     * @param {object} [scope] The object, which will be set to 'this' inside the callback's body.
     * @param {(number | string)} [position] The position of this callback at the timeline.
     * If you pass a number this will set the position as absolute time.
     * ```ts
     * // Insert exactly 3 seconds from the start of the timeline
     * timeline.call(sprite.scale, {x: 0.75 }, 3);
     * ```
     * If you pass a string or omit this parameter:
     * The default position is "+=0", which just means "at the end" of the current timeline.
     * "<" position means the start of previous animation. Think of < as a pointer back to the start of the previous animation.
     * ">" position means the end of the previous animation. Think of > as a pointer to the end of the previous animation.
     * "someLabel" means label position. If the label doesn't exist, it'll be added to the end of the timeline.
     * 
     * You can additionaly use the '+=', '-=' and '%' to any of the string positions.
     * Examples: "+=1", "-=0.3", "someLable+=3", "+=50%", "<25%" and so on.
     * @return {*}  {Timeline}
     * @memberof Timeline
     */
    public call(callback: Callback, params?: any[], scope?: object, position?: number | string): Timeline {
        let paramsToUse = params;
        if (params && params.length == 0) {
            paramsToUse = undefined;
        }

        let scopeToUse = scope ? scope : this._timeline.vars.callbackScope;
        if (scopeToUse) {
            this._timeline.call(callback.bind(scopeToUse, paramsToUse), paramsToUse, position);
        } else {
            this._timeline.call(callback, paramsToUse, position);
        }

        return this;
    }

    /**
     * The same as Timeline.call() method, using a different arguments order.
     * See its documentation for more details.
     *
     */
    public addCallback(callback: Callback, position?: number | string, params?: any[], scope?: object): Timeline {
        this.call(callback, params, scope, position);
        return this;
    }

    /**
     * Sets a callback of the timeline.
     *
     * @param {TweenCallbackType} type A value from the TweenCallbackType enum.
     * @param {Callback} callback The callback to execute, when this event is fired.
     * @param {any[]} [params] The arguments to pass to the callback, when the event is fired.
     * @param {object} [scope] The object, which will refer to 'this' at the callback's body.
     * @return {*}  {Timeline} Self
     * @memberof Timeline
     */
    public eventCallback(type: TweenCallbackType, callback: Callback, params?: any[], scope?: object): Timeline {
        let paramsToUse = params;
        if (params && params.length == 0) {
            paramsToUse = undefined;
        }

        this._timeline.eventCallback(<gsap.CallbackType>(type.toString()), callback, paramsToUse, scope);
        return this;
    }

    /**
     * Begins playing the animation forward from the beginning, optionally including the starting delay.
     *
     * ```ts
     * anim.restart();     // repeats the animation NOT including the starting delay
     * anim.restart(true); // repeats the animation including the starting delay
     * ```
     *
     * @param {boolean} [includeDelay=false]
     * @param {boolean} [suppressEvents=false]
     * @return {*} {Timeline} Self
     * @memberof Timeline
     */
    public restart(includeDelay: boolean = false, suppressEvents: boolean = false): Timeline {
        this._timeline.restart(includeDelay, suppressEvents);
        return this;
    }

    /**
     * Sets the current time of the timeline, using a time or a label.
     *
     * ```ts
     * anim.seek(1); // moves the playhead to the 1 second mark
     * ```
     *
     * @param {(number | string)} time The time in seconds or the label to seek for.
     * @param {boolean} [suppressEvents=false] Pass true to suppress the event firing.
     * @return {*}  {Timeline} Self
     * @memberof Timeline
     */
    public seek(time: number | string, suppressEvents: boolean = false): Timeline {
        this._timeline.seek(time, suppressEvents);
        return this;
    }

    private unwrap(target: TweenTarget): any {
        // Currently type script does not provide a way to check
        // whether an object inherits particular interface.
        // IWrapper in our case.
        if (target instanceof Tween || target instanceof Timeline) {
            return target.getWrappedObject();
        } else if (target instanceof Array) {
            let results = [];
            target.forEach(item => {
                if (item instanceof Tween || item instanceof Timeline) {
                    results.push(item.getWrappedObject());
                } else {
                    results.push(item);
                }
            });

            return results;
        }

        return target;
    }

    private addChild(value: TimelineChild, position?: number | string, onlyToChildCollection: boolean = false) {
        let child: any;
        if (value instanceof Tween) {
            let tweenType = value.getTweenType();
            value.kill();

            switch (tweenType) {
                case TweenType.DelayedCall: {
                    child = value.vars.onComplete.bind(value.vars.callbackScope, value.vars.onCompleteParams);
                    if (!onlyToChildCollection) {
                        let parsedPos = this.parsePosition(this._timeline, position, child);
                        this._timeline.add(child, parsedPos + value.vars.delay);
                    }
                }

                break;

                case TweenType.To: {
                    this._timeline.to(value.targets, value.vars, position);
                    child = this._timeline.recent();
                    if (onlyToChildCollection) {
                        this._timeline.remove(child);
                    }
                }

                break;

                case TweenType.From: {
                    this._timeline.from(value.targets, value.vars, position);
                    child = this._timeline.recent();
                    if (onlyToChildCollection) {
                        this._timeline.remove(child);
                    }
                }

                break;

                case TweenType.FromTo: {
                    this._timeline.fromTo(value.targets, value.vars, value.secondVars, position);
                    child = this._timeline.recent();
                    if (onlyToChildCollection) {
                        this._timeline.remove(child);
                    }
                }

                break;
            }
        } else {
            child = this.unwrap(value);
            if (!onlyToChildCollection) {
                this._timeline.add(child, position);
            }
        }
           
        if (this._children.has(child)) {
            this._children.get(child).push(value);
        } else {
            this._children.set(child, new Array<TimelineChild>(value));
        }
    }

    private parsePosition(animation, position, percentAnimation) {
        var _zeroPosition = {
            _start: 0,
            endTime: function() { return 0; },
            totalDuration: function() { return 0; }
        };

        var _bigNum = 1e8;
        var _isString = function _isString(value) {
            return typeof value === "string";
        };
        
        var labels = animation.labels,
            recent = animation._recent || _zeroPosition,
            clippedDuration = animation.duration() >= _bigNum ? recent.endTime(false) : animation._dur,
            i,
            offset,
            isPercent;
    
        if (_isString(position) && (isNaN(position) || position in labels)) {
          offset = position.charAt(0);
          isPercent = position.substr(-1) === "%";
          i = position.indexOf("=");
    
          if (offset === "<" || offset === ">") {
            i >= 0 && (position = position.replace(/=/, ""));
            return (offset === "<" ? recent._start : recent.endTime(recent._repeat >= 0)) + (parseFloat(position.substr(1)) || 0) * (isPercent ? (i < 0 ? recent : percentAnimation).totalDuration() / 100 : 1);
          }
    
          if (i < 0) {
            position in labels || (labels[position] = clippedDuration);
            return labels[position];
          }
    
          offset = parseFloat(position.charAt(i - 1) + position.substr(i + 1));
    
          if (isPercent && percentAnimation) {
            offset = offset / 100 * (Array.isArray(percentAnimation) ? percentAnimation[0] : percentAnimation).totalDuration();
          }
    
          return i > 1 ? this.parsePosition(animation, position.substr(0, i - 1), percentAnimation) + offset : clippedDuration + offset;
        }
    
        return position == null ? clippedDuration : +position;
    }
}

export interface TimelineVars extends AnimationVars {
    autoRemoveChildren?: boolean;
    defaults?: TweenVars;
    delay?: number;
    smoothChildTiming?: boolean;
}

export type TimelineChild = string | Timeline | Tween | Callback | Array<string | Timeline | Tween | Callback>;